import { Injectable, TemplateRef } from "@angular/core";
import * as Dialogs from "../dialogs";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { ComponentType } from "@angular/cdk/portal";
import Utils from "../../core/shared/utils";
import * as Models from "../models";
import { ISOTService, SOTDialogResult } from './ISOTService';

const DIALOG_PANEL_CLASS = "sot-dialogpanelclass";
const DIALOG_BACKDROP_CLASS = "sot-overlaybackdropclass";

/** Service zum verwalten und öffnen von Dialogen */
@Injectable()
export class SOTDialogService {

  constructor(private _MatDialog: MatDialog) {
  }

  /** hilfs Kapsel um diealoge zu erzeugen, kann bei bedarf auf public gesetzt werden */
  private openDialog<T, R>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, data?: any): MatDialogRef<T, R> {
    const config: MatDialogConfig = {
      disableClose: true,
      panelClass: DIALOG_PANEL_CLASS,
      backdropClass: DIALOG_BACKDROP_CLASS,
      hasBackdrop: true,
    };
    if (data) {
      config.data = data;
    }
    const dialog = this._MatDialog.open(componentOrTemplateRef, config);
    return dialog;
  }

  /** hilfs Kapsel um diealoge zu erzeugen, kann bei bedarf auf public gesetzt werde */
  private openSimpleMessageDialog<R>(config: Dialogs.SimpleMessageDialogConfig): MatDialogRef<Dialogs.SimpleMessageDialogComponent, R> {
    return this.openDialog(Dialogs.SimpleMessageDialogComponent, config);
  }

  /** Offnet ein Dialog mit infinity loader, muss per software geschlossen
   * @returns {void} no return
   */
  private openLoaderDialog(): MatDialogRef<Dialogs.LoaderDialogComponent, void> {
    return this.openDialog(Dialogs.LoaderDialogComponent);
  }

  /** hält den aktuel angezeigten loader dialog als ref */
  private _loaderDialog_ref: MatDialogRef<Dialogs.LoaderDialogComponent, void>;

  /** wird der loader dialog momentan angezeigt */
  public get is_loader_dialog_shown(): boolean {
    return !Utils.IsNull(this._loaderDialog_ref);
  }

  /** öffnet den Loader dialog an */
  public showLoaderDialog(): void {
    const me = this;
    if (!me._loaderDialog_ref) {
      me._loaderDialog_ref = me.openLoaderDialog();
    }
  }

  /** schließt den Loader dialog an */
  public hideLoaderDialog(): void {
    const me = this;
    if (me._loaderDialog_ref) {
      me._loaderDialog_ref.close();
      me._loaderDialog_ref = null;
    }
  }

  /** öffnet ein Artikel ohne abfragen (single) in einem Dialog */
  public openSingleProductDialog<R>(config: Dialogs.SingleProductDialogConfig): MatDialogRef<Dialogs.SingleProductDialogComponent, R> {
    return this.openDialog(Dialogs.SingleProductDialogComponent, config);
  }

  /** öffnet einen Dialog welcher die Subartikel anzeigt */
  public openProductSubDialog(config: Dialogs.ProductSubDialogConfig): MatDialogRef<Dialogs.ProductSubDialogComponent, Dialogs.ProductSubDialogResult> {
    return this.openDialog(Dialogs.ProductSubDialogComponent, config);
  }

  public openProductRecommendationDialog<R>(config: Dialogs.ProductRecommendationDialogConfig): MatDialogRef<Dialogs.ProductRecommendationDialogComponent, R> {
    return this.openDialog(Dialogs.ProductRecommendationDialogComponent, config);
  }

  public openCardlimitDialog<R>(productTagsConfig: Dialogs.CardlimitDialogConfig): MatDialogRef<Dialogs.CardlimitDialogComponent, R> {
    return this.openDialog(Dialogs.CardlimitDialogComponent, productTagsConfig);
  }

  public openArticleopenNumberPadDialogTagFilterDialog(ProductTagFilterDialogConfig: Dialogs.ProductTagFilterDialogConfig): MatDialogRef<Dialogs.ProductTagFilterDialogComponent, string> {
    return this.openDialog(Dialogs.ProductTagFilterDialogComponent, ProductTagFilterDialogConfig);
  }

  public openNumberPadDialog<R>(config: Dialogs.NumberPadDialogComponentConfig): MatDialogRef<Dialogs.NumberPadDialogComponent, R> {
    return this.openDialog(Dialogs.NumberPadDialogComponent, config);
  }

  public openArticleTagFilterDialog(ProductTagFilterDialogConfig: Dialogs.ProductTagFilterDialogConfig): MatDialogRef<Dialogs.ProductTagFilterDialogComponent, string> {
    return this.openDialog(Dialogs.ProductTagFilterDialogComponent, ProductTagFilterDialogConfig);
  }

  public openCouponNumberPadDialog(CouponNumberPadDialogConfig: Dialogs.CouponNumberPadDialogConfig): MatDialogRef<Dialogs.CouponNumberPadDialogComponent, string> {
    return this.openDialog(Dialogs.CouponNumberPadDialogComponent, CouponNumberPadDialogConfig);
  }

  public openAccessabiltyDialog<R>(accessibilityoptionsConfig: Dialogs.AccessibilityDialogConfig): MatDialogRef<Dialogs.AccessibilityDialogComponent, R> {
    return this.openDialog(Dialogs.AccessibilityDialogComponent, accessibilityoptionsConfig);
  }

  public openScanVoucherDialog(scanVoucherData: Dialogs.ScanVoucherData): MatDialogRef<Dialogs.VoucherScanDialogComponent, "voucher" | "manual" | "cancel"> {
    return this.openDialog(Dialogs.VoucherScanDialogComponent, scanVoucherData);
  }

  public openVoucherNumberPadDialog(voucherNumberPadDialogConfig: Dialogs.voucherPinNumberPadDialogConfig): MatDialogRef<Dialogs.VoucherNumberPadDialogComponent, string | "cancel"> {
    return this.openDialog(Dialogs.VoucherNumberPadDialogComponent, voucherNumberPadDialogConfig);
  }

  public openVoucherPinNumberPadDialog(voucherPinNumberPadDialogConfig: Dialogs.voucherPinNumberPadDialogConfig): MatDialogRef<Dialogs.VoucherPinNumberPadDialogComponent, string | "cancel"> {
    return this.openDialog(Dialogs.VoucherPinNumberPadDialogComponent, voucherPinNumberPadDialogConfig);
  }

  public openVoucherPayDialog(voucherConfig: Dialogs.VoucherDialogDataConfig): MatDialogRef<Dialogs.VoucherPayDialogComponent, Models.PaymentType> {
    return this.openDialog(Dialogs.VoucherPayDialogComponent, voucherConfig);
  }

  public openAgeDialog(config: Dialogs.AgeDialogConfig): MatDialogRef<Dialogs.AgeDialogComponent, "yes" | "no"> {
    return this.openDialog(Dialogs.AgeDialogComponent, config);
  }

  public openBonusActionDialog(config: Dialogs.IBonusActionsDialogComponentConfig): MatDialogRef<Dialogs.BonusActionsDialogComponent, void> {
    return this.openDialog(Dialogs.BonusActionsDialogComponent, config);
  }

  /**
   * User war inaktiv, nun wird der dialog abfragen ob der benutzer noch da ist, mit einem timeout für die antwort
   * @param timeout secunden, bis der dialog sich selbst ständig schliest
   * @returns {string} "yes" or "no" (witch user clicked) or "timeout" wenn der timeout abgelaufen ist
   */
  public openStillThereDialog(timeout: number, sotService: ISOTService): MatDialogRef<Dialogs.CountdownDialogComponent, "yes" | "no" | "timeout"> {
    const config = {
      timeout: timeout,
      sot_service: sotService,
    }
    return this.openDialog(Dialogs.CountdownDialogComponent, config);
  }

  /** Dialog wenn Zahlungsabwicklung fehlgeschlagen ist
   * @param allow_pay_at_pos zeigt den butten, zahlen am POS
   * @returns {string} "retry_pay", "pos_pay" or "cancel" (witch user clicked)
   */
  public openPayFailDialog(allow_pay_at_pos: boolean, sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "retry_pay" | "pos_pay" | "cancel"> {
    const config = {
      langkey: "dialog_pay_fail",
      buttons_in_row: false, // show in column 
      buttons: [{
        langkey: "dialog_pay_fail_retry_pay",
        onClick: (dialog) => { dialog.close("retry_pay") }
      }],
      default_button: 0, // dialog_pay_fail_retry_pay
      sot_service: sotService,
    };
    if (allow_pay_at_pos) {
      config.buttons.push({
        langkey: "dialog_pay_fail_pos_pay",
        onClick: (dialog) => { dialog.close("pos_pay") }
      });
    }
    config.buttons.push({
      langkey: "dialog_pay_fail_cancel",
      onClick: (dialog) => { dialog.close("cancel") }
    });
    return this.openSimpleMessageDialog<"retry_pay" | "pos_pay" | "cancel">(config);
  }

  /** Dialog wenn Zahlungsabwicklung fehlgeschlagen ist
   * @returns {string} "rescan" | "continue" (witch user clicked)
   */
  public openTippingDialog(basketPrice: number, sotService: ISOTService): MatDialogRef<Dialogs.TipDialogComponent> {
    const config = {
      basketprice: basketPrice,
      sot_service: sotService,
    }
    return this.openDialog(Dialogs.TipDialogComponent, config);
  }

  /** Dialog wenn Zahlungsabwicklung fehlgeschlagen ist
   * @returns {string} "rescan" | "continue" (witch user clicked)
   */
  public openWrongCardDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "rescan" | "continue"> {
    const config = {
      langkey: "process_wrong_card",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "process_wrong_card_rescan",
        onClick: (dialog) => { dialog.close("rescan"); }
      }, {
        langkey: "process_wrong_card_continue",
        onClick: (dialog) => { dialog.close("continue"); }
      }],
      default_button: 0, // process_wrong_card_rescan
      sot_service: sotService,
    };
    return this.openSimpleMessageDialog<"rescan" | "continue">(config);
  }

  /** Dialog bei Fehler beim Vorgangskartelesen
   * @returns {string} "ok"
   */
  public openCardCouldNotBeReadDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent> {
    const config = {
      langkey: "dialog_cannot_be_read",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "ok",
        onClick: (dialog) => { dialog.close(); }
      }],
      default_button: 0, // ok
      sot_service: sotService,
    };
    return this.openSimpleMessageDialog(config);
  }

  /** Dialog bei Fehler beim Pager scannen
   * @returns {string} "ok"
   */
  public openPagerCouldNotBeReadDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent> {
    const config = {
      langkey: "dialog_pager_cannot_be_read",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "ok",
        onClick: (dialog) => { dialog.close(); }
      }],
      default_button: 0, // ok
      sot_service: sotService,
    };
    return this.openSimpleMessageDialog(config);
  }

  /** Dialog for error message when scanning the coupon
 * @returns {string} "rescan" | "continue" (witch user clicked)
 */
  public openCouponCouldNotBeReadDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "rescan" | "continue"> {
    const config = {
      langkey: "coupons_invalid_code",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "coupons_enter_new_code",
        onClick: (dialog) => { dialog.close("rescan"); }
      }, {
        langkey: "coupons_cancel",
        onClick: (dialog) => { dialog.close("continue"); }
      },],
      default_button: 0,
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"rescan" | "continue">(config);
  }

  /** Dialog when the scanned coupon is already in the cart; navigates to basket on confirmation. */
  public openCouponInCartDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "cancel" | "go_to_basket"> {
    const me = this;
    const config: Dialogs.SimpleMessageDialogConfig = {
      langkey: "coupons_in_cart",
      buttons_in_row: false,
      buttons: [
        {
          langkey: "button_cancel",
          onClick: (dialog) => { dialog.close("cancel"); }
        },
        {
          langkey: "coupons_in_cart_go_to_basket",
          onClick: (dialog) => { dialog.close("go_to_basket"); }
        },
      ],
      default_button: 1,
      sot_service: sotService,
    };
    return me.openSimpleMessageDialog<"cancel" | "go_to_basket">(config);
  }

  /**Dialog for error message if "scan product" not available
 * @returns {string} "rescan" | "continue" (witch user clicked)
 */
  public openCouponArticleNotAvailable(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "rescan" | "continue"> {
    const config = {
      langkey: "coupons_notavailable_code",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "coupons_enter_new_code",
        onClick: (dialog) => { dialog.close("rescan"); }
      }, {
        langkey: "coupons_cancel",
        onClick: (dialog) => { dialog.close("continue"); }
      },],
      default_button: 0,
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"rescan" | "continue">(config);
  }

  /** Dialog for error message when scanning the voucher
 * @returns {string} "rescan" | "continue"
 */
  public OpenVoucherCouldNotBeReadDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "rescan" | "continue"> {
    const config = {
      langkey: "voucherscan_not_valid",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "voucherscan_not_valid_retry",
        onClick: (dialog) => { dialog.close("rescan"); }
      }, {
        langkey: "button_cancel",
        onClick: (dialog) => { dialog.close("continue"); }
      },],
      default_button: 0,
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"rescan" | "continue">(config);
  }


  /** 
   * User versucht die Bestellung abzubrechen
   * @returns {string} "yes" or "no" (witch user clicked)
   */
  public openOrderCancelDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "yes" | "no"> {
    const config = {
      langkey: "dialog_sure_cancel",
      buttons: [{
        langkey: "dialog_sure_cancel_no",
        onClick: (dialog) => { dialog.close("no") }
      }, {
        langkey: "dialog_sure_cancel_yes",
        onClick: (dialog) => { dialog.close("yes") },
      }],
      default_button: 1, // dialog_sure_cancel_yes
      sot_service: sotService
    }
    return this.openSimpleMessageDialog<"yes" | "no">(config);
  }

  /** 
   * Select redeem coupons or benefits dialog
   * @param sotService The SOT service instance
   * @returns Dialog reference with SOTDialogResult
   */
  public openCouponOrBenefitDialog(sotService: ISOTService): MatDialogRef<Dialogs.SelectionDialogComponent, SOTDialogResult> {
    const config: Dialogs.ChoiceDialogConfig = {
      langkey: "dialog_choose_text",
      buttons_in_row: false,
      buttons: [{
        langkey: "coupons_redeem",
        icon: "sot/sot-icon.svg#sot-coupon",
        onClick: (dialog) => { dialog.close(SOTDialogResult.ScanCoupon); }
      }, {
        langkey: "benefit_button_redeem",
        icon: "sot/sot-icon.svg#sot-bonus",
        onClick: (dialog) => { dialog.close(SOTDialogResult.RedeemBenefit); }
      },
      {
        langkey: "button_cancel",
        icon: "sot/sot-icon.svg#sot-close",
        onClick: (dialog) => { dialog.close(SOTDialogResult.Back); }
      }],
      highlighted_button_index: [0, 1],
      default_button_index: 2,
      sot_service: sotService,
    };
    return this.openDialog(Dialogs.SelectionDialogComponent, config);
  }

  /**
   * Opens a dialog informing the user that the currently selected benefits
   * are not fully covered by the available bonus points.
   * @returns {string} "continue"
   */
  public openPointsCoverageWarningDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "continue"> {
    const config = {
      langkey: "dialog_benefits_notcovered",
      buttons_in_row: false, // show in column
      buttons: [{
        langkey: "button_continue",
        onClick: (dialog) => { dialog.close("continue"); }
      },],
      default_button: 0,
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"continue">(config);
  }

  /** 
 * User versucht die Service zu rufen
 * @returns {string} "yes" or "no" (witch user clicked)
 */
  public openServiceCallDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "yes" | "no"> {
    const config = {
      langkey: "dialog_service_call",
      buttons: [{
        langkey: "dialog_service_call_no",
        onClick: (dialog) => { dialog.close("no") }
      }, {
        langkey: "dialog_service_call_yes",
        onClick: (dialog) => { dialog.close("yes") },
      }],
      default_button: 1, // dialog_sure_cancel_yes
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"yes" | "no">(config);
  }

  /** Drucker ausgefallen dialog, ohne möglichkeit ihn als user zu schließen, muss durch software erfolgen 
   * @returns {void} no return
  */
  public openPrinterErrorDialog(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, void> {
    const config = {
      langkey: "dialog_printer_error",
      buttons: [], // no buttons
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<void>(config);
  }

  /** 
 * user can confirm to reset tag filter for single extra
 * @returns {string} "yes" or "no" (witch user clicked)
 */
  public openResetExtraTagFilter(sotService: ISOTService): MatDialogRef<Dialogs.SimpleMessageDialogComponent, "yes" | "no"> {
    const config = {
      langkey: "tags_article_unlock_text",
      buttons: [{
        langkey: "dialog_sure_cancel_no",
        onClick: (dialog) => { dialog.close("no") }
      }, {
        langkey: "dialog_sure_cancel_yes",
        onClick: (dialog) => { dialog.close("yes") },
      }],
      default_button: 1, // dialog_sure_cancel_yes
      sot_service: sotService,
    }
    return this.openSimpleMessageDialog<"yes" | "no">(config);
  }
}
